import { useCallback, useEffect, useRef, useState } from 'react';

import { useStoreContext } from 'features/store';

import { getStoredOrderId } from 'experiments/checkout-v2/control/pages/cart/payment/order-payment/util';
import { shouldSettleCheckoutRepriceFromPricedOrder } from 'experiments/checkout-v2/treatment/features/checkout/hooks/delivery-reprice-utils';
import {
  DeliveryStatus,
  IPriceDeliveryInput,
  IPriceOrderInput,
  usePriceOrderMutation,
} from 'generated/graphql-gateway';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useOrderStatus } from 'hooks/order-status';
import { useCart } from 'hooks/use-cart';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { actions, useAppDispatch } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { OrderStatus } from 'state/order/types';
import { useServiceModeLive } from 'state/service-mode';
import { buildPriceDeliveryInput, buildPriceOrderInput } from 'utils/cart';
import { computeOtherDiscountAmount } from 'utils/cart/helper';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { logger } from 'utils/logger';
import { TraceName, startTrace, stopTrace } from 'utils/performance/trace';
import { isDelivery } from 'utils/service-mode';

import { IPriceOrderParams, IUsePriceOrderParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1000;
const PRICING_RETRY_LIMIT = 2; // Attempt reprice 3 times

const failureStatuses = {
  delivery: [DeliveryStatus.QUOTE_ERROR, DeliveryStatus.QUOTE_UNAVAILABLE],
  pos: [OrderStatus.PRICE_ERROR],
};

// note that serverOrder contains statuses for commit and price order, we have to make sure that
// the statuses we check here belongs to pricing errors statuses
export const priceOrderErrorStatuses = [...failureStatuses.delivery, ...failureStatuses.pos];

export const usePriceOrder = ({
  cartEntries,
  enabled = true,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  updateTipAmount,
}: IUsePriceOrderParams) => {
  const { calculateCartTotalWithDiscount } = useCart();
  const {
    checkoutRepriceRequestId,
    checkoutRepriceRequestCompletedId,
    deliveryAddress,
    quoteId,
    setCheckoutRepriceRequestCompletedId,
    setQuoteId,
  } = useOrderContext();
  const { store } = useStoreContext();
  const serviceMode = useServiceModeLive();
  const isSignUpAfterCart = useIsSignUpAfterCartEnabled();
  const [fireMutation, mutationResult] = usePriceOrderMutation({
    context: { shouldNotBatch: true },
  });
  const enablePaymentErrorContinue = useFlag(LaunchDarklyFlag.ENABLE_PAYMENT_ERROR_CONTINUE);
  const { vendor } = usePosVendor();
  const [didOrderPricingRetryFail, setDidOrderPricingRetryFail] = useState(false);
  const orderPricingAttemptsRef = useRef<number>(0);
  const dispatch = useAppDispatch();

  // Guards against stale pricing responses overwriting the latest rbiOrderId.
  // When multiple priceOrder mutations are in-flight (e.g., initial pricing + reward-triggered
  // reprice), Apollo's useMutation updates data with whichever response arrives last
  // chronologically, not the last requested. This ref + state pair ensures only the most
  // recent mutation's response is accepted.
  const pricingSequenceRef = useRef(0);
  const [acceptedRbiOrderId, setAcceptedRbiOrderId] = useState<string | null>(null);
  // Capture the reprice request id for this pricing attempt so a late poll
  // success cannot advance completion past a newer address/store change.
  const pricingRepriceRequestIdRef = useRef(0);

  const successStatuses = enablePaymentErrorContinue
    ? [
        OrderStatus.PRICE_SUCCESSFUL,
        // This will prevent this price order query from being called again
        // if the order is already inserted
        OrderStatus.INSERT_SUCCESSFUL,
        OrderStatus.PAYMENT_ERROR,
        OrderStatus.VALIDATION_ERROR,
      ]
    : [OrderStatus.PRICE_SUCCESSFUL, OrderStatus.VALIDATION_ERROR];

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount?.(cartEntries) || {};

  const existingOrder = enabled ? getStoredOrderId() : null;
  const rbiOrderId = enabled ? acceptedRbiOrderId ?? existingOrder ?? null : null;

  const orderStatusQueryResult = useOrderStatus({
    failureStatuses,
    onSuccess(rbiOrder) {
      // Store pricedOrderId in Redux when pricing succeeds
      dispatch(actions.ordering.setPricedOrderId(rbiOrder.rbiOrderId));

      const requestIdForAttempt = pricingRepriceRequestIdRef.current;
      if (
        shouldSettleCheckoutRepriceFromPricedOrder({
          checkoutRepriceRequestId: requestIdForAttempt,
          checkoutRepriceRequestCompletedId,
          serviceMode,
          quoteId,
          deliveryAddress,
          serverOrder: rbiOrder,
          selectedStoreNumber: store?.number ?? null,
        })
      ) {
        if (rbiOrder.delivery?.quoteId) {
          setQuoteId(rbiOrder.delivery.quoteId);
        }
        setCheckoutRepriceRequestCompletedId(current => Math.max(current, requestIdForAttempt));
      }

      if (isDelivery(rbiOrder.cart.serviceMode)) {
        const otherDiscountAmount = computeOtherDiscountAmount(rbiOrder.cart.discounts);
        if (updateTipAmount) {
          updateTipAmount({
            subTotalCents: rbiOrder.cart.subTotalCents,
            otherDiscountAmount,
          });
        }
      }
      stopTrace(TraceName.PRICE_ORDER, { rbiOrderId: rbiOrderId ?? '' });
    },
    orderStatusPollInterval,
    rbiOrderId,
    successStatuses: {
      delivery: DeliveryStatus.QUOTE_SUCCESSFUL,
      pos: successStatuses,
    },
    skip: !enabled || (!rbiOrderId && isCartTotalNegative && mutationResult.loading),
  });

  const {
    orderStatus,
    serverOrder,
    failure: priceOrderFailure,
    success: priceOrderSuccess,
    isPricingFailureRecoverable,
    isLoading,
  } = orderStatusQueryResult;

  const isDeliveryOrderLoading =
    isDelivery(serverOrder?.cart.serviceMode) && !serverOrder?.delivery;

  // priceOrderFailure && isPricingFailureRecoverable toggle during repricing,
  // so we check orderPricingAttemptsRef > 0 to maintain a stable true value
  // throughout the retry process, preventing UI flicker in dependent components.
  const isOrderPricingRetryInProgress =
    Boolean(serverOrder?.status && priceOrderErrorStatuses.includes(serverOrder.status)) &&
    ((priceOrderFailure && isPricingFailureRecoverable) || orderPricingAttemptsRef.current > 0) &&
    !didOrderPricingRetryFail;

  // serverOrder?.rbiOrderId !== rbiOrderId check covers the brief moment during
  // which the mutation finished but the order status query hasn't yet started.
  // Without this, `loading` is set to `false` momentarily, which causes the UI
  // to become enabled for that brief period only to then become disabled when
  // the order status query begins.
  const loading =
    mutationResult.loading ||
    (serverOrder?.rbiOrderId || null) !== (rbiOrderId || null) ||
    isLoading ||
    isDeliveryOrderLoading;

  const acceptMutationResult = useCallback(
    (sequence: number, result: { data?: { priceOrder?: { rbiOrderId?: string } } | null }) => {
      if (pricingSequenceRef.current !== sequence) {
        logger.warn({
          message: '[usePriceOrder] Discarding stale priceOrder response',
          context: {
            expectedSequence: pricingSequenceRef.current,
            receivedSequence: sequence,
          },
        });
        return;
      }
      const orderId = result.data?.priceOrder?.rbiOrderId ?? null;
      if (orderId) {
        setAcceptedRbiOrderId(orderId);
      }
    },
    []
  );

  // Note that this actually just requests a price order. the result of this process is a polling we
  // do in the `useOrderStatus` hook.
  const priceOrder = useCallback(
    (options: Omit<IPriceOrderParams, 'cartEntries' | 'redeemReward'>) => {
      if (!enabled) {
        return;
      }

      const isCartInvalid = !cartEntries.length || isCartTotalNegative;

      if (isCartInvalid || isSignUpAfterCart) {
        return;
      }

      const input = buildPriceOrderInput({ cartEntries, vendor, options });

      if (!input) {
        return;
      }

      const delivery = buildPriceDeliveryInput(options, quoteId);
      const mutationVariables = { input, delivery };

      syncStorage.setObject(SyncStorageKey.PRICE_ORDER_INPUT, mutationVariables);

      // Dispatch action to indicate pricing has started
      dispatch(actions.ordering.setIsPricingOrder(true));
      pricingRepriceRequestIdRef.current = checkoutRepriceRequestId;

      const currentSequence = ++pricingSequenceRef.current;

      startTrace(TraceName.PRICE_ORDER);

      return fireMutation({
        variables: mutationVariables,
      }).then(result => {
        acceptMutationResult(currentSequence, result);
        return result;
      });
    },
    [
      cartEntries,
      checkoutRepriceRequestId,
      enabled,
      isSignUpAfterCart,
      isCartTotalNegative,
      vendor,
      quoteId,
      fireMutation,
      dispatch,
      acceptMutationResult,
    ]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (priceOrderFailure) {
      if (!serverOrder || !priceOrderErrorStatuses.includes(serverOrder.status)) {
        return;
      }

      const isMaxRetryLimitReached = orderPricingAttemptsRef.current === PRICING_RETRY_LIMIT;

      if (isMaxRetryLimitReached) {
        setDidOrderPricingRetryFail(true);
        return;
      }

      const mutationVariables = syncStorage.getObject<{
        input: IPriceOrderInput;
        delivery: IPriceDeliveryInput;
      }>(SyncStorageKey.PRICE_ORDER_INPUT);

      if (mutationVariables === null) {
        logger.error('Price order mutation variables are null, cannot retry pricing.');
        return;
      }

      orderPricingAttemptsRef.current += 1;

      const retrySequence = pricingSequenceRef.current;

      startTrace(TraceName.PRICE_ORDER);

      fireMutation({
        variables: mutationVariables,
      }).then(result => {
        acceptMutationResult(retrySequence, result);
      });
    }
  }, [
    priceOrderFailure,
    enabled,
    isPricingFailureRecoverable,
    isOrderPricingRetryInProgress,
    fireMutation,
    serverOrder,
    acceptMutationResult,
  ]);

  const setIsRepricing = useCallback(() => {
    setDidOrderPricingRetryFail(false);
    orderPricingAttemptsRef.current = 0;
  }, [setDidOrderPricingRetryFail]);

  // Set isPricingOrder to false when pricing completes (success or failure)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (priceOrderSuccess || priceOrderFailure) {
      dispatch(actions.ordering.setIsPricingOrder(false));
    }
  }, [enabled, priceOrderSuccess, priceOrderFailure, dispatch]);

  return {
    loading,
    orderStatus,
    priceOrderFailure,
    priceOrderSuccess,
    priceResult: mutationResult,
    rbiOrderId,
    serverOrder,
    priceOrder,
    isOrderPricingRetryInProgress,
    isPricingFailureRecoverable,
    didOrderPricingRetryFail,
    setIsRepricing,
  } as const;
};
